import type { IHiddenFieldsBelow } from './NamespaceApi';
import type {
  IAuthResponse,
  IDeploymentSetupStatus,
  IOAuthAvailability,
  IOAuthIdentityLink,
  IPasswordResetReview,
  IRecentlyAddedSignInMethod,
  PasswordResetLinkStateType,
  IUser,
  OAuthProviderType,
} from '@featit/shared';
import { TERMS_VERSION } from '@featit/shared/legal';
import {
  type DesktopRecordClientType,
  type DeviceSigningJwkType,
} from '@featit/shared/signed-decisions/record';
import { type DeviceKeyBindingStrengthType } from '@featit/shared/signed-decisions/registry';
import type { AxiosResponseHeaders } from 'axios';
import { ApiService } from './ApiService';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import { signInDeviceHeaders } from './signInDevice';
import { noteSessionServerTiming } from '@/shell/bootReport';
import { isDesktopRuntime } from '@/desktop/desktopDetection';
import { expectRecord } from './answerShape';
import { desktopReturnSchemeOverride } from '@/desktop/desktopReturnScheme';
import { loadDesktopSignInBinding } from '@/desktop/desktopSessionLazy';
import { DesktopForeignCodeError } from '@/desktop/desktopSignInErrors';

/** What `/setup` submits (F-0160 §11, I-0638). */
export interface ICompleteSetupInput {
  token: string;
  email: string;
  name: string;
  password: string;
}

/** A passkey made in the browser, with the challenge it answered (F-0160 §11.5, AC-72). */
export interface ISetupPasskeyProof {
  challengeId: string;
  response: RegistrationResponseJSON;
}

/** What the browser door submits: a name, an address, and a password or a passkey. */
export interface IFirstRunSetupInput {
  email: string;
  name: string;
  password?: string;
  passkey?: ISetupPasskeyProof;
}

/** What a claim link submits: the link's token, and a password or a passkey. */
export interface IClaimAccountInput {
  token: string;
  password?: string;
  passkey?: ISetupPasskeyProof;
}

/** Whose account a claim link is for. */
export interface IClaimPeek {
  email: string;
  name: string;
  expiresAt: string;
}

export type SetupPasskeyOptionsInputType =
  | { purpose: 'first-run'; email: string; name: string }
  | { purpose: 'claim'; token: string };

export interface IPasskey {
  id: string;
  name: string;
  transports: string[];
  deviceType: string;
  backedUp: boolean;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IPasskeyRegistrationOptions {
  challengeId: string;
  options: PublicKeyCredentialCreationOptionsJSON;
  label: string | null;
  /** The origin the deployment verifies passkeys against. Absent from an older deployment. */
  origin?: string;
}

/** F-0489 slice 3: the device key a passkey sign-in bound, as the server recorded it. */
export interface IBoundDeviceSigningKey {
  thumbprint: string;
  bindingStrength: DeviceKeyBindingStrengthType;
  deploymentId: string;
}

export interface IPasskeyAuthenticationOptions {
  challengeId: string;
  options: PublicKeyCredentialRequestOptionsJSON;
  /** The origin the deployment verifies passkeys against. Absent from an older deployment. */
  origin?: string;
}

/** What deleting this account would do, counted rather than described. */
export interface IAccountDeletionPreview {
  anonymised: {
    comments: number;
    workItemsAuthored: number;
    workItemsResponsible: number;
    activities: number;
    documents: number;
    commitRecords: number;
  };
  removed: {
    notifications: number;
    workAreaMemberships: number;
    savedViews: number;
    agents: number;
    passkeys: number;
    sessions: number;
  };
  blockedBy: Array<{
    id: string;
    name: string;
    memberCount: number;
    resolution: 'transfer' | 'delete-with-it';
    /**
     * T-0650 (§4b): a System Admin's Delete now deletes this work area with the account, because it
     * is in the person's own namespace and nobody else is in it. It still blocks everything else.
     */
    goesWithOperatorErasure?: boolean;
  }>;
  /**
   * Blockers that are not an owned work area (F-0224 §4): the last owner of an organisation, the
   * only System Admin, or somebody else's work area still in your personal namespace.
   */
  roleBlockers: IAccountDeletionRoleBlocker[];
  /**
   * Organisations you are the last owner of whose deletion is already scheduled (I-0971, §4a). They
   * no longer block; the account is deleted no earlier than the latest of them.
   */
  organisationsPendingDeletion: Array<{
    id: string;
    name: string;
    path: string;
    scheduledFor: string;
  }>;
  workAreasTouched: number;
  /** Days between asking for erasure and it happening (§6). */
  graceDays: number;
  /** The erasure already asked for, or null. */
  pending: IPendingAccountDeletion | null;
}

export interface IAccountDeletionRoleBlocker {
  kind:
    | 'organisation'
    | 'system-admin'
    | 'hosted-work-area'
    | 'personal-subscription'
    | 'hidden-fields-below';
  id: string | null;
  name: string | null;
  remedy: string;
  /** For `organisation` (I-0971, §4a): what it holds. */
  organisation?: { path: string; workAreas: number; members: number };
  /** For `organisation`: the two ways out a client offers as actions. */
  ways?: Array<'make-owner' | 'delete-organisation'>;
  /** For `organisation`: the day its pending deletion is due (YYYY-MM-DD), or null (I-1988). */
  scheduledFor?: string | null;
  /** For `hidden-fields-below` (I-1988): the remedy as data, worded by `hiddenFieldsBelowText`. */
  hiddenFields?: IHiddenFieldsBelow;
}

export interface IPendingAccountDeletion {
  requestedAt: string;
  scheduledFor: string;
  requestedBy: 'self' | 'system-admin';
}

export type AccountDeletionRequestResultType =
  | { status: 'scheduled'; scheduledFor: string }
  | { status: 'erased' };

/** A downloaded personal-data archive, with the name the server gave it. */
export interface IAccountExportDownload {
  blob: Blob;
  fileName: string;
}

/** An export reads every table that names you; give it longer than an ordinary request. */
const ACCOUNT_EXPORT_TIMEOUT_MS = 120_000;

function fileNameFrom(disposition: unknown, fallback: string): string {
  const match = /filename="?([^";]+)"?/.exec(typeof disposition === 'string' ? disposition : '');
  return match?.[1] ?? fallback;
}

/** F-0809 (EAI-1): what `/-/join` may show of a deployment invitation before sign-up. */
export interface IDeploymentInvitationPreview {
  maskedEmail: string;
  invitedBy: string | null;
  expiresAt: string;
}

/** What sign-up answers since F-0160 AC-25a (D-0041 = a): no tokens until the address is confirmed. */
export interface IRegistrationPending {
  verificationRequired: true;
  email: string;
}

class AuthApiService extends ApiService {
  // I-1034: every sign-in names the install it comes from, so Devices can say which one it is and
  // a sign-in from the same browser replaces the session the last one left.
  /**
   * I-2181: with an invitation link, the body carries the link and no address — the server signs in
   * as the address the link was sent to, so nothing typed can name another account.
   */
  async login(email: string, password: string, invitation?: string | null): Promise<IAuthResponse> {
    return this.post('/auth/login', invitation ? { password, invitation } : { email, password }, {
      headers: await signInDeviceHeaders(),
    });
  }

  /**
   * I-2181: the address an invitation link was sent to, masked, or null for one that names none or
   * no longer works. A sign-in or sign-up from the link is fixed to it.
   */
  async invitedAddress(invitation: string): Promise<string | null> {
    try {
      const preview = await this.post<{ invitedAddress?: string | null }>('/invitations/preview', {
        token: invitation,
      });
      return preview?.invitedAddress ?? null;
    } catch {
      return null;
    }
  }

  /**
   * F-0809 (EAI-1): what `/-/join` shows of a System Admin's invitation to the deployment: the masked
   * address, who invited and until when. A link that does not open is refused with its code.
   */
  deploymentInvitationPreview(token: string): Promise<IDeploymentInvitationPreview> {
    return this.post('/deployment-invitations/preview', { token });
  }

  /**
   * F-0160 AC-25a: an account, and **no session** — the address has to be confirmed first. The
   * answer names the address the link went to.
   */
  register(
    email: string,
    password: string,
    name: string,
    invitation?: string | null,
    place?: string | null,
    deploymentInvitation?: string | null,
  ): Promise<IRegistrationPending> {
    // F-0329: the invitation link's token, when the sign-up comes from one.
    return this.post('/auth/register', {
      // I-2181: none with a link sent to an address — the server signs up the link's own.
      ...(email ? { email } : {}),
      password,
      name,
      ...(invitation ? { invitation } : {}),
      // D-0418 LK-8: an individual link's place, so the sign-up grants that place only.
      ...(invitation && place ? { invitationPlace: place } : {}),
      // F-0809 (EAI-1): a System Admin's invitation to the deployment, from `/-/join`.
      ...(deploymentInvitation ? { deploymentInvitation } : {}),
      // F-0554, AC-TA2: the form said "By creating an account you accept the Terms"; this version.
      acceptedTermsVersion: TERMS_VERSION,
    });
  }

  /** F-0554, AC-TA4: accept the terms in force; answers the own account. */
  acceptTerms(version: string): Promise<IUser> {
    return this.post('/auth/me/terms-acceptance', { version });
  }

  refresh(): Promise<{ accessToken: string }> {
    return this.post('/auth/refresh');
  }

  /** F-0160 AC-15/16/17. `email` is the address confirmed (I-2229), for the sign-in that follows. */
  confirmEmailVerification(token: string): Promise<{ verified: true; email?: string | null }> {
    return this.post('/auth/email-verification/confirm', { token });
  }

  /**
   * F-0160 AC-25b. For a person the sign-in door refused with `email_unverified`, who therefore has
   * no session: the same email and password they just tried prove the account before a link is sent.
   */
  resendEmailVerificationForSignIn(
    email: string,
    password: string,
  ): Promise<{ sent: true } | { sent: false; alreadyVerified: true }> {
    return this.post('/auth/email-verification/resend-for-sign-in', { email, password });
  }

  /** F-0160 AC-18. Authenticated — works on the signed-in account, not on an address. */
  resendEmailVerification(): Promise<{ sent: true }> {
    return this.post('/auth/email-verification/resend');
  }

  /** F-0160 AC-20. Always resolves `{ ok: true }`, whether or not the address is registered. */
  requestPasswordReset(email: string): Promise<{ ok: true }> {
    return this.post('/auth/password-reset/request', { email });
  }

  /** I-2003: whether a reset link can still be used, asked without spending it. */
  checkPasswordReset(token: string): Promise<{ state: PasswordResetLinkStateType }> {
    return this.post('/auth/password-reset/check', { token });
  }

  /**
   * F-0160 AC-21/22/23. T-0633 (F-0563 D3): the answer carries the review of what was added in the
   * last 30 days; a backend from before it answers `{ ok: true }` alone.
   */
  confirmPasswordReset(
    token: string,
    newPassword: string,
  ): Promise<{ ok: true; review?: IPasswordResetReview }> {
    return this.post('/auth/password-reset/confirm', { token, newPassword });
  }

  /** T-0633 (F-0563 D7): removes one method the reset's review listed. The grant is the credential. */
  removeReviewedSignInMethod(
    grant: string,
    kind: IRecentlyAddedSignInMethod['kind'],
    id: string,
  ): Promise<{ removed: true }> {
    return this.post('/auth/password-reset/review/remove', { grant, kind, id });
  }

  /**
   * F-0160 §11 (I-0638): whether this deployment is waiting for its first administrator. A fact
   * about the deployment, the same for every visitor.
   */
  setupStatus(): Promise<IDeploymentSetupStatus> {
    return this.get('/auth/setup');
  }

  /** F-0160 §11.5: the browser door, in the window after a start. Signs the administrator in. */
  firstRunSetup(input: IFirstRunSetupInput): Promise<IAuthResponse> {
    return this.post('/auth/setup/first-run', input);
  }

  /** F-0160 AC-72: a passkey ceremony for the browser door or a claim, before anything is written. */
  setupPasskeyOptions(
    input: SetupPasskeyOptionsInputType,
  ): Promise<Omit<IPasskeyRegistrationOptions, 'label'>> {
    return this.post('/auth/setup/passkey-options', input);
  }

  /** F-0160 §11.5: whose account a claim link is for, without spending it. */
  peekClaim(token: string): Promise<IClaimPeek> {
    return this.post('/auth/setup/claim/peek', { token });
  }

  /** F-0160 AC-76, AC-77: spend a claim link, set a password or passkey, and sign in. */
  claimAccount(input: IClaimAccountInput): Promise<IAuthResponse> {
    return this.post('/auth/setup/claim', input);
  }

  /** F-0160 §11: create the first administrator with a one-time setup token, and sign in. */
  completeSetup(input: ICompleteSetupInput): Promise<IAuthResponse> {
    return this.post('/auth/setup', input);
  }

  me(): Promise<IUser> {
    return this.get('/auth/me', {
      // T-0559: the boot report splits the session check into server time and transit.
      transformResponse: [
        ...[this.client.defaults.transformResponse ?? []].flat(),
        (data: unknown, headers?: AxiosResponseHeaders) => {
          noteSessionServerTiming(headers?.['server-timing']);
          return data;
        },
      ],
    });
  }

  passkeys(): Promise<IPasskey[]> {
    return this.get('/auth/passkeys');
  }

  beginPasskeyRegistration(name?: string): Promise<IPasskeyRegistrationOptions> {
    return this.post('/auth/passkeys/registration/options', { name });
  }

  verifyPasskeyRegistration(data: {
    challengeId: string;
    response: RegistrationResponseJSON;
    name?: string;
  }): Promise<IPasskey> {
    return this.post('/auth/passkeys/registration/verify', data);
  }

  deletePasskey(id: string): Promise<{ ok: boolean }> {
    return this.delete(`/auth/passkeys/${encodeURIComponent(id)}`);
  }

  /** `deviceKey`: F-0489 slice 3, the public JWK of the device key this sign-in binds. */
  beginPasskeyAuthentication(
    email?: string,
    deviceKey?: DeviceSigningJwkType,
  ): Promise<IPasskeyAuthenticationOptions> {
    return this.post(
      '/auth/passkeys/authentication/options',
      deviceKey ? { email, deviceKey } : { email },
    );
  }

  async verifyPasskeyAuthentication(data: {
    challengeId: string;
    response: AuthenticationResponseJSON;
    deviceKeyBinding?: {
      jwk: DeviceSigningJwkType;
      client: 'web' | DesktopRecordClientType;
      signature: string;
    };
  }): Promise<IAuthResponse & { deviceSigningKey?: IBoundDeviceSigningKey | null }> {
    return this.post('/auth/passkeys/authentication/verify', data, {
      headers: await signInDeviceHeaders(),
    });
  }

  async accountDeletionPreview(): Promise<IAccountDeletionPreview> {
    return expectRecord<IAccountDeletionPreview>(
      await this.get('/auth/me/deletion'),
      'Account deletion preview',
      ['anonymised', 'removed'],
    );
  }

  /** Ask for erasure. It is scheduled after the grace period and signs you out everywhere. */
  deleteAccount(): Promise<AccountDeletionRequestResultType> {
    return this.delete('/auth/me');
  }

  /** Withdraw a pending erasure request. */
  cancelAccountDeletion(): Promise<{ cancelled: boolean }> {
    return this.delete('/auth/me/deletion');
  }

  /** Just the pending request, for the app-wide banner: one indexed lookup, not the preview. */
  accountDeletionStatus(): Promise<{ pending: IPendingAccountDeletion | null }> {
    return this.get('/auth/me/deletion/status');
  }

  /** Everything held about you, as a ZIP with a manifest (Art. 15, Art. 20). */
  async exportAccountData(): Promise<IAccountExportDownload> {
    const response = await this.client.get('/auth/me/export', {
      responseType: 'blob',
      timeout: ACCOUNT_EXPORT_TIMEOUT_MS,
    });
    return {
      blob: response.data as Blob,
      fileName: fileNameFrom(response.headers['content-disposition'], 'featit-personal-data.zip'),
    };
  }

  authorizeCliLogin(data: {
    redirectUri: string;
    state: string;
  }): Promise<{ redirectUri: string; expiresAt: string }> {
    return this.post('/auth/cli/authorize', data);
  }

  /**
   * F-0228: a one-time code for the phone app, bound to its PKCE challenge. The answer is the
   * `featit://auth/callback?code&state` the page hands back to the app.
   */
  authorizeMobileSignIn(data: {
    code_challenge: string;
    code_challenge_method: string;
    state: string;
    redirect_uri: string;
  }): Promise<{ redirectUri: string; expiresAt: string }> {
    return this.post('/auth/mobile/authorize', data);
  }

  /**
   * Which sign-in providers this deployment offers.
   *
   * Asked before the sign-in screen draws anything. A deployment with none configured shows the
   * password form and the passkey button exactly as it did before this feature — no dead buttons,
   * and nobody reading a "not configured" error that was never theirs to fix.
   */
  oauthProviders(): Promise<IOAuthAvailability> {
    return this.get('/auth/oauth/providers');
  }

  /**
   * Begin a provider sign-in.
   *
   * A POST that returns a URL, rather than a link that redirects. A `GET` anybody could point a
   * browser at would begin a sign-in the person never asked for; this way the navigation is
   * something the app decided to do.
   *
   * `desktopReturn` (I-1078, AC-15): set from `isDesktopRuntime()` here, not by the caller — the
   * desktop app has no choice in the matter, and a browser tab has no listener for the scheme the
   * backend would otherwise send it to.
   */
  async startOAuthSignIn(
    provider: OAuthProviderType,
    redirect?: string,
  ): Promise<{ authorizationUrl: string }> {
    const desktopReturn = isDesktopRuntime();
    // I-1182: the desktop app sends only the hash of a verifier it keeps (desktopSignInBinding.ts).
    // T-0894: the binding loads only here, in the desktop app; a browser tab never fetches it.
    const binding = desktopReturn ? await loadDesktopSignInBinding() : undefined;
    const desktopChallenge = binding ? await binding.beginDesktopSignInBinding() : undefined;
    const desktopReturnScheme = desktopReturn ? await desktopReturnSchemeOverride() : undefined;
    const started = await this.post<{ authorizationUrl: string; desktopChallengeEcho?: boolean }>(
      `/auth/oauth/${encodeURIComponent(provider)}/start`,
      {
        redirect,
        desktopReturn,
        ...(desktopReturnScheme ? { desktopReturnScheme } : {}),
        ...(desktopChallenge ? { desktopChallenge } : {}),
      },
    );
    // I-1437, ADR-160 AC-31: the check of the return is fixed here, before the browser opens, from
    // what this deployment declares. An older one declares nothing, and its return goes unchecked.
    if (binding && desktopChallenge && started.desktopChallengeEcho !== true) {
      binding.markDesktopSignInUnchecked(desktopChallenge);
    }
    return { authorizationUrl: started.authorizationUrl };
  }

  /**
   * F-0819, in the desktop app: the address of the confirm page, carrying only the hash of a verifier
   * this app keeps, as a provider sign-in does (I-1182).
   */
  async desktopBrowserSignInAddress(deploymentAddress: string): Promise<string> {
    const binding = await loadDesktopSignInBinding();
    const url = new URL('/desktop-auth', deploymentAddress);
    url.searchParams.set('challenge', await binding.beginDesktopSignInBinding());
    const scheme = await desktopReturnSchemeOverride();
    if (scheme) url.searchParams.set('scheme', scheme);
    return url.toString();
  }

  /** F-0819: the confirm press, from the person's own browser session. */
  authorizeDesktopSignIn(data: {
    desktopChallenge: string;
    desktopReturnScheme?: string;
  }): Promise<{ redirectUri: string }> {
    return this.post('/auth/oauth/desktop/authorize', data);
  }

  /** Begin connecting a provider to the account already signed in. Needs the bearer token. */
  async startOAuthLink(provider: OAuthProviderType): Promise<{ authorizationUrl: string }> {
    const desktopReturnScheme = await desktopReturnSchemeOverride();
    return this.post(`/auth/oauth/${encodeURIComponent(provider)}/link/start`, {
      desktopReturn: isDesktopRuntime(),
      ...(desktopReturnScheme ? { desktopReturnScheme } : {}),
    });
  }

  /**
   * Swap the one-time code from the redirect for the real credential.
   *
   * ⛔ The reason the redirect carries a code rather than the token: a URL lands in the edge access
   * log, in browser history and in the `Referer` of the next request.
   */
  async exchangeOAuthCode(code: string, desktopChallenge?: string | null): Promise<IAuthResponse> {
    // I-1182: in the desktop app a code is redeemed only with the verifier this app kept when it
    // started the sign-in. None kept means this app did not start one: refused here, with nothing
    // sent, so the foreign code is never even presented. I-1437: and only a link naming the
    // challenge of that sign-in takes it, so a foreign link leaves it for the person's own return.
    let desktopVerifier: string | undefined;
    if (isDesktopRuntime()) {
      const { takeDesktopSignInVerifier } = await loadDesktopSignInBinding();
      const kept = takeDesktopSignInVerifier(desktopChallenge);
      if (!kept) throw new DesktopForeignCodeError();
      desktopVerifier = kept;
    }
    return this.post(
      '/auth/oauth/exchange',
      { code, ...(desktopVerifier ? { desktopVerifier } : {}) },
      { headers: await signInDeviceHeaders() },
    );
  }

  /**
   * Swap a verification redemption code for a read-only session (I-0326, §3.3 step 3). Same shape
   * as {@link exchangeOAuthCode} for the same reason: the code is single-use, stored server-side, and
   * deleted before it is validated, so a second call with the same code is refused, not replayed.
   *
   * `workAreas` rides in this same response rather than a follow-up call to
   * {@link verificationScope} (I-0353, AC-S): a second round trip opened a real gap for the rest of
   * the app's own post-sign-in effects to pick the ordinary default work area first and win the
   * race to the address bar, which is the landing-outside-scope defect this AC exists to fix —
   * measured happening, not merely theorised.
   */
  redeemVerificationCode(
    code: string,
  ): Promise<IAuthResponse & { workAreas: Array<{ id: string; slug: string; name: string }> }> {
    return this.post('/auth/verify-session/redeem', { code });
  }

  /**
   * What the calling session's own bindings name (I-0353, AC-S) — for anything that needs the
   * answer outside the redemption moment itself. `VerifySessionPage` does not use this; it reads
   * `workAreas` off {@link redeemVerificationCode}'s own response instead, in the same round trip.
   */
  verificationScope(): Promise<{ workAreas: Array<{ id: string; slug: string; name: string }> }> {
    return this.get('/auth/verify-session/scope');
  }

  oauthIdentities(): Promise<IOAuthIdentityLink[]> {
    return this.get('/auth/oauth/identities');
  }

  unlinkOAuthProvider(provider: OAuthProviderType): Promise<{ disconnected: boolean }> {
    return this.delete(`/auth/oauth/${encodeURIComponent(provider)}`);
  }

  /** F-0750: the own name, as the server keeps it (trimmed, at most 80 characters). */
  setOwnName(name: string): Promise<{ name: string }> {
    return this.patch('/auth/me/name', { name });
  }

  /** Where FEATit writes. An empty value clears it back to the sign-in address. */
  setContactEmail(contactEmail: string | null): Promise<IContactAddressStanding> {
    return this.patch('/auth/me/contact-email', { contactEmail });
  }

  /** F-0557: the sign-in address, and whether a change is waiting or can still be undone. */
  signInAddressStanding(): Promise<ISignInAddressStanding> {
    return this.get('/auth/sign-in-address');
  }

  /**
   * F-0557: ask to change the sign-in address. Answers `{ requested: true }` whether or not the
   * address is taken (AC-6), so the caller has nothing to tell the two apart by.
   */
  requestSignInAddressChange(input: {
    newEmail: string;
    reauthToken: string;
    endOtherSessions: boolean;
  }): Promise<{ requested: true }> {
    return this.post('/auth/sign-in-address/change', input);
  }

  /** F-0557: the link mailed to the new address. The token in the body is the credential. */
  confirmSignInAddress(token: string): Promise<{ changed: true; email: string }> {
    return this.post('/auth/sign-in-address/confirm', { token });
  }

  /** F-0557: the link mailed to the old address. The token in the body is the credential. */
  undoSignInAddress(token: string): Promise<{ undone: true; email: string }> {
    return this.post('/auth/sign-in-address/undo', { token });
  }

  /** I-1395: whether mail to this person's sign-in address is stopped, and why. */
  mailStanding(): Promise<IMailStanding> {
    return this.get('/auth/me/mail-standing');
  }

  /** I-1395: lift a bounce and send one verification message to the address. */
  sendMailAgain(): Promise<IMailStanding> {
    return this.post('/auth/me/mail-standing/send-again', {});
  }
}

/** F-0557: `GET /auth/sign-in-address`. */
export interface ISignInAddressStanding {
  email: string;
  /** A request waiting for its link to be opened. */
  pending: { newEmail: string; expiresAt: string } | null;
  /** A change that took effect and can still be undone. */
  undoable: { previousEmail: string; until: string } | null;
  undoWindowDays: number;
}

/** Why mail to a person's address is stopped, and whether they may ask for it again (I-1395). */
export interface IMailStanding {
  address: string;
  /** Null when mail is not stopped for a delivery problem. */
  reason: 'bounced' | 'complained' | null;
  /** The message sent by "Send again" bounced as well. */
  bouncedAgain: boolean;
  canSendAgain: boolean;
  /** When "Send again" has room again, if its limit is used up. */
  retryAt: string | null;
}

/** Where FEATit writes to somebody, and what happened last time it tried. */
export interface IContactAddressStanding {
  signInEmail: string;
  contactEmail: string | null;
  /** The one that a message would actually go to. */
  effective: string;
  emailProvidedBy: OAuthProviderType | null;
  deliveryStatus: 'unknown' | 'delivered' | 'deferred' | 'undeliverable';
  deliveryUpdatedAt: string | null;
  /** ⛔ True only for a hard failure. A deferral is a full mailbox, not a reason to nag anybody. */
  needsAnotherContactEmail: boolean;
}

export const authApi = new AuthApiService();
